import { useQuery } from "@tanstack/react-query";
import { Building2, CheckCircle2, Users, CalendarClock, TrendingDown, Home, Clock, BadgeCheck } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "../../lib/api";
import Seo from "../../components/Seo";

const COLORS = ["#3b3e8c", "#6366f1", "#818cf8", "#b45309", "#2563eb", "#e11d48"];

function Metric({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <div className="font-heading text-3xl font-extrabold text-slate-900 mt-3">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { data } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/admin/dashboard")).data });
  const m = data?.metrics || {};
  return (
    <div>
      <Seo title="Admin Dashboard | Faridabad Property" />
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Dashboard</h1>
      <p className="text-slate-500">Overview of your inventory and leads.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Metric icon={Building2} label="Total Properties" value={m.total_properties ?? 0} color="bg-indigo-50 text-indigo-700" />
        <Metric icon={CheckCircle2} label="Active Properties" value={m.active_properties ?? 0} color="bg-indigo-50 text-indigo-700" />
        <Metric icon={Users} label="New Leads" value={m.new_leads ?? 0} color="bg-blue-50 text-blue-700" />
        <Metric icon={Clock} label="Today's Leads" value={m.today_leads ?? 0} color="bg-blue-50 text-blue-700" />
        <Metric icon={CalendarClock} label="Upcoming Visits" value={m.upcoming_visits ?? 0} color="bg-amber-50 text-amber-700" />
        <Metric icon={BadgeCheck} label="Completed Visits" value={m.completed_visits ?? 0} color="bg-amber-50 text-amber-700" />
        <Metric icon={Home} label="Sold Properties" value={m.sold_properties ?? 0} color="bg-slate-100 text-slate-700" />
        <Metric icon={TrendingDown} label="Price Reduced" value={m.price_reduced ?? 0} color="bg-rose-50 text-rose-700" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Most Viewed Properties</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.views_chart || []}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip />
              <Bar dataKey="views" fill="#3b3e8c" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Lead Sources</h3>
          {data?.lead_sources?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.lead_sources} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {data.lead_sources.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-slate-400 text-sm h-[260px] flex items-center justify-center">No lead data yet</div>}
        </div>
      </div>
    </div>
  );
}
